/**
 * Comparing a report's table with what CSRD holds now, and bringing it in line.
 *
 * Pure: a table and what CSRD answered in, a table and a count out. Nothing
 * here talks to CSRD or to the store, which is what lets the arithmetic of a
 * refresh be tested without either.
 *
 * Two cases, kept apart because they cost the person different things:
 *
 *   * **the same layout** (the same rows, each as wide as before). Only the
 *     words in the cells change, so every colour, alignment and bold someone
 *     gave the table stays where it is.
 *   * **a different layout** (a row added in CSRD's template, a column
 *     dropped). The table is laid out again as CSRD has it. There is no cell to
 *     say which of the old ones a new one would be, so what was done to the
 *     look is followed by the row's label and the column's heading.
 *
 * Locking the table is what makes the first safe: nobody has edited a figure
 * in it, so there is no hand-made correction to protect.
 */

import type { CsrdBlock, CsrdTablePull } from '@/api/csrd'
import type { CsrdLink, TableCell, TableSection } from '@/domain/section'
import { restyled } from '@/features/csrdLink/restyle'

/** What a refresh would do. */
export interface Comparison {
  /** Cells whose words differ from what CSRD holds now. */
  readonly changed: number
  /** Whether the layout itself differs, so the table would be laid out again. */
  readonly reshaped: boolean
}

function sameLayout(section: TableSection, table: CsrdBlock): boolean {
  if (section.header.length !== table.header.length) return false
  if (section.rows.length !== table.rows.length) return false
  return section.rows.every((row, at) => row.length === table.rows[at]?.length)
}

/** How many cells of a grid differ from the one it would become. */
function differing(
  now: readonly (readonly TableCell[])[],
  next: readonly (readonly { readonly value: string }[])[],
): number {
  let count = 0
  next.forEach((row, at) =>
    row.forEach((cell, column) => {
      if ((now[at]?.[column]?.value ?? null) !== cell.value) count += 1
    }),
  )
  return count
}

/** What reading the table again from CSRD would change in this one. */
export function compareToCsrd(section: TableSection, pull: CsrdTablePull): Comparison {
  const now = [section.header, ...section.rows]
  const next = [pull.table.header, ...pull.table.rows]
  return { changed: differing(now, next), reshaped: !sameLayout(section, pull.table) }
}

/** The link as it stands once this reading has been taken in. */
function refreshedLink(link: CsrdLink, pull: CsrdTablePull): CsrdLink {
  return {
    ...link,
    pulledAt: pull.pulledAt,
    collected: pull.disclosure.collected ?? link.collected,
    controlled: pull.disclosure.controlled ?? link.controlled,
    audited: pull.disclosure.audited ?? link.audited,
  }
}

/**
 * The table, brought in line with what CSRD holds.
 *
 * A table with no link is returned as it was: there is nothing to read it
 * from, and this is not where that is decided.
 */
export function applyPull(section: TableSection, pull: CsrdTablePull): TableSection {
  const link = section.csrd
  if (!link) return section

  if (sameLayout(section, pull.table)) {
    const refill = (cells: readonly TableCell[], from: CsrdBlock['header']): TableCell[] =>
      cells.map((cell, at) => {
        const incoming = from[at]
        if (!incoming) return cell
        const next: TableCell = { ...cell, value: incoming.value }
        if (incoming.numeric && !cell.numeric) next.numeric = true
        return next
      })

    return {
      ...section,
      header: refill(section.header, pull.table.header),
      rows: section.rows.map((row, at) => refill(row, pull.table.rows[at] ?? [])),
      csrd: refreshedLink(link, pull),
    }
  }

  return {
    ...section,
    // Laid out as CSRD has it now, dressed the way the old one was (see `restyle.ts`).
    ...restyled(section, pull.table),
    csrd: refreshedLink(link, pull),
  }
}
